"use client"

import {useEffect, useRef} from "react";
import {useLocale} from "next-intl";
import {useAppSelector} from "@/lib/hooks";
import {
    identifyUser,
    initMixpanel,
    isMixpanelEnabled,
    registerSuperProperties,
    resetIdentity,
} from "@/lib/analytics/mixpanel";
import {getAnonymousId} from "@/lib/analytics/anonymous-id";
import {getSessionId} from "@/lib/analytics/session";
import {getAttributionProperties} from "@/lib/analytics/attribution";

/**
 * Bootstraps Mixpanel: SDK init, the common-properties (super-property) layer,
 * and identity. Mounted once in the locale layout, alongside <CioInitializer/>.
 *
 * Client-only by construction ("use client" + effects), so the SDK never runs
 * during server rendering.
 */
export default function MixpanelInitializer() {
    const userId = useAppSelector((state) => state.auth.userId);
    const country = useAppSelector((state) => state.auth.country);
    const isMobile = useAppSelector((state) => state.ui.isMobile);
    const userCurrency = useAppSelector((state) => state.cashier.userCurrency);
    const locale = useLocale();

    // Tracks the previous identity so we can tell a real logout (player → none)
    // apart from an anonymous visitor who was simply never logged in.
    const previousUserId = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!isMixpanelEnabled()) return;
        initMixpanel();
    }, []);

    // Identity and the common-properties (super-property) layer, in one effect
    // because their ORDER matters: `mixpanel.reset()` wipes super-properties as
    // well as the identity, so a logout must reset *before* the properties are
    // registered. Split across two effects, React would run them in declaration
    // order and the logout reset would silently clear the bag that had just been
    // written — leaving every subsequent anonymous event without common
    // properties until the next locale/currency change happened to re-register.
    //
    // On login/registration: identify by the JWT `sub` (the stable DB key), which
    // merges the anonymous pre-registration journey into the player profile.
    //
    // On logout: reset, so the next visitor on a shared device starts clean.
    // `reset()` mints a NEW distinct_id — so it must fire ONLY on a genuine
    // player → anonymous transition. Calling it whenever `userId` is falsy would
    // re-roll the anonymous identity on every page load and destroy the
    // pre-registration journey we are trying to preserve.
    useEffect(() => {
        if (!isMixpanelEnabled()) return;

        const hadUser = previousUserId.current;
        previousUserId.current = userId;

        const isLogout = !userId && !!hadUser;
        if (isLogout) {
            resetIdentity();
        }

        registerSuperProperties({
            platform: "web",
            device_type: isMobile ? "mobile" : "desktop",
            environment: process.env.NEXT_PUBLIC_APP_ENV ?? "unknown",
            app_version: process.env.NEXT_PUBLIC_APP_VERSION ?? "unknown",
            // The spec's enum is `frontend | backend | provider` — it splits events
            // by who emitted them, not by which product they came from.
            source: "frontend",
            language: locale,
            country: country || "unknown",
            currency: userCurrency,
            // Explicit `null` while anonymous, so the property stays typed in the
            // Lexicon instead of appearing only on logged-in events.
            user_id: userId ?? null,
            anonymous_id: getAnonymousId(),
            session_id: getSessionId(),
            // Attribution (spec §5) — captured on landing by <RouteObserver/> and
            // read back from storage here. It belongs in THIS bag as well: on
            // logout `resetIdentity()` wipes every super-property, and this effect
            // is what re-registers them. Left out, a player who logs out would
            // keep browsing as an unattributed visitor for the rest of the visit.
            ...getAttributionProperties(),
        });

        if (userId) {
            identifyUser(userId, {language: locale, country: country || undefined});
        }
    }, [userId, isMobile, locale, country, userCurrency]);

    return null;
}
